import {
  COOKING_ORDER,
  type CookingMethod,
  gramsOfPieces,
  piecesOf,
  type RecipeCooking,
  recipePer100gCooked,
  retainedOilG,
  withCookingOil,
  yieldRatio,
} from '@gramas/shared'
import { Button, Card, EmptyState, List, ListRow, MacroPreview, Sheet } from '@gramas/ui'
import { useLiveQuery } from 'dexie-react-hooks'
import { useEffect, useMemo, useRef, useState } from 'react'
import { fetchFoodRemote } from '../api/foods.js'
import { db, type LocalFood, type LocalRecipe } from '../db/dexie.js'
import {
  cacheFood,
  deleteRecipe,
  foodDisplayName,
  foodPer100g,
  listRecipes,
  liveIngredients,
  saveRecipe,
} from '../db/repo.js'
import { sassLine } from '../sass/engine.js'
import { useSassOnce } from '../sass/useSassOnce.js'
import { syncNow } from '../sync/engine.js'
import { CookedWeightField } from './sheets/CookedWeightField.js'
import { CHIP_LABEL } from './sheets/CookingPicker.js'
import { FoodPickerSheet } from './sheets/FoodPickerSheet.js'
import { LogRecipeSheet } from './sheets/LogRecipeSheet.js'
import { RecipeImportSheet } from './sheets/RecipeImportSheet.js'

interface DraftIngredient {
  key: string
  food: LocalFood
  rawWeightG: number
  /** This one ingredient's pan. A dish is rarely cooked one way throughout. */
  cooking: CookingMethod
}

/**
 * The amount of one ingredient.
 *
 * Holds what is typed as text while the field has focus, for the same reason
 * `NumberField` does: re-deriving the value from the parsed number swallowed
 * the separator, so "2,5" could only ever be typed as "25" — and a recipe with
 * 25 g of salt in it is not a rounding error. The unit follows the food rather
 * than always reading "g", because a drink's figures are per 100 ml and asking
 * for grams of it quietly changed what the number meant.
 */
function IngredientWeightField({
  value,
  unit,
  pieceWeightG,
  onChange,
}: {
  value: number
  unit: LocalFood['unit']
  /** Set on a food people count: the field then asks for a count. */
  pieceWeightG: number | null
  onChange: (value: number) => void
}) {
  const [typed, setTyped] = useState<string | null>(null)
  // Two eggs, not 120 g of egg. Grams stay what is stored — the field converts
  // on the way in and out, so nothing downstream learns a second unit.
  const shown = pieceWeightG != null ? piecesOf(value, pieceWeightG) : value
  return (
    <input
      inputMode="decimal"
      aria-label={`Kiekis (${pieceWeightG != null ? 'vnt.' : unit})`}
      value={typed ?? String(shown).replace('.', ',')}
      onChange={(e) => {
        setTyped(e.target.value)
        const parsed = Number.parseFloat(e.target.value.replace(',', '.'))
        const next = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0
        onChange(pieceWeightG != null ? gramsOfPieces(next, pieceWeightG) : next)
      }}
      onBlur={() => setTyped(null)}
      className="tnum h-8 w-20 rounded-[var(--radius-control)] border border-line bg-bg px-2 text-right text-base text-ink"
    />
  )
}

export function Recipes() {
  const recipes = useLiveQuery(() => listRecipes(), [], [])
  const emptyTitle = useSassOnce(
    recipes.length === 0 ? 'recipes' : null,
    () => sassLine('empty_list', { surface: 'recipes' }) ?? 'Receptų dar nėra',
  )
  // Held by id rather than by value: editing a recipe rewrites the row, and a
  // captured snapshot would put the old ingredient list back on screen the
  // moment the editor closes.
  const [editingId, setEditingId] = useState<string | 'new' | null>(null)
  const [loggingId, setLoggingId] = useState<string | null>(null)
  const [importing, setImporting] = useState(false)

  const editing = editingId === 'new' ? 'new' : (recipes.find((r) => r.id === editingId) ?? null)
  const logging = recipes.find((r) => r.id === loggingId) ?? null
  const setEditing = (next: LocalRecipe | 'new' | null) =>
    setEditingId(next === 'new' ? 'new' : (next?.id ?? null))
  const setLogging = (next: LocalRecipe | null) => setLoggingId(next?.id ?? null)

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between px-4 py-3">
        <p className="text-sm text-mute">
          Įveskite žalius ingredientus ir gatavo patiekalo svorį — maistingumas perskaičiuojamas
          pagal jį.
        </p>
      </div>

      {recipes.length === 0 ? (
        <EmptyState
          title={emptyTitle ?? ''}
          hint="Receptas leidžia sverti gatavą patiekalą, o ne kiekvieną ingredientą atskirai."
          action={
            <div className="flex flex-col gap-2">
              <Button variant="primary" onClick={() => setEditing('new')}>
                Sukurti receptą
              </Button>
              <Button onClick={() => setImporting(true)}>Iš nuorodos</Button>
            </div>
          }
        />
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <List className="border-y border-line bg-surface">
            {recipes.map((recipe, i) => (
              <ListRow
                key={recipe.id}
                last={i === recipes.length - 1}
                title={recipe.name}
                subtitle={`${Math.round(recipe.cookedWeightG)} g gatavo · ${Math.round(recipe.kcal)} kcal/100 g`}
                value={`${Math.round((recipe.kcal * recipe.cookedWeightG) / 100)}`}
                meta="kcal viso"
                onActivate={() => setLogging(recipe)}
              />
            ))}
          </List>
          <div className="flex gap-2 px-4 py-4">
            <Button block onClick={() => setEditing('new')}>
              Naujas receptas
            </Button>
            {/* Most recipes people cook are already written down somewhere. */}
            <Button block onClick={() => setImporting(true)}>
              Iš nuorodos
            </Button>
          </div>
        </div>
      )}

      <RecipeImportSheet open={importing} onOpenChange={setImporting} />

      <RecipeEditor
        open={editing != null}
        recipe={editing === 'new' ? null : editing}
        onOpenChange={(open) => !open && setEditing(null)}
      />
      <LogRecipeSheet
        open={logging != null && editing == null}
        recipe={logging}
        onOpenChange={(open) => !open && setLogging(null)}
        // A batch is cooked once and eaten from for days, and the second
        // portion is exactly when you notice you want less of something. The
        // editor was reachable only for new recipes, so a saved one was frozen.
        onEdit={(recipe) => setEditing(recipe)}
        onDelete={async (id) => {
          await deleteRecipe(id)
          void syncNow()
          setLogging(null)
        }}
      />
    </div>
  )
}

function RecipeEditor({
  open,
  recipe,
  onOpenChange,
}: {
  open: boolean
  recipe: LocalRecipe | null
  onOpenChange: (open: boolean) => void
}) {
  const [name, setName] = useState('')
  const [cookedWeightG, setCookedWeightG] = useState<number | null>(null)
  const [ingredients, setIngredients] = useState<DraftIngredient[]>([])
  const [picking, setPicking] = useState(false)
  const [saving, setSaving] = useState(false)
  /** Ingredients whose food row could not be found on the phone or the server. */
  const [missing, setMissing] = useState(0)
  // Not stored on the recipe: it exists to produce a first guess at the
  // finished weight, and once that weight has been entered it says nothing the
  // number does not. A recipe reopened later already has its answer.
  const [method, setMethod] = useState<RecipeCooking>('unweighed')

  const derived = useMemo(() => {
    const cooked = ingredients
      .filter((i) => i.rawWeightG > 0)
      .map((i) => ({
        per100g: foodPer100g(i.food),
        rawWeightG: i.rawWeightG,
        cooking: i.cooking,
      }))
    if (cooked.length === 0) return null
    // The oil the pans left behind is one more thing in the pot: it has weight,
    // it has calories, and the finished dish on the scale already contains it.
    const resolved = withCookingOil(cooked)
    const oilG =
      resolved.reduce((sum, i) => sum + i.rawWeightG, 0) -
      cooked.reduce((sum, i) => sum + i.rawWeightG, 0)
    const rawTotal = resolved.reduce((sum, i) => sum + i.rawWeightG, 0)
    // Nothing has been weighed yet: the pot is what went into it. Recipes are
    // written before the dish comes off the heat, and refusing to save one
    // until it has been weighed loses the ingredient list that was the work.
    const cookedTotal = cookedWeightG && cookedWeightG > 0 ? cookedWeightG : rawTotal
    if (cookedTotal <= 0) return null
    return {
      per100g: recipePer100gCooked(resolved, cookedTotal),
      yield: yieldRatio(resolved, cookedTotal),
      rawTotal,
      cooked: cookedTotal,
      oilG,
      weighed: cookedWeightG != null && cookedWeightG > 0,
    }
  }, [ingredients, cookedWeightG])

  const recipeId = recipe?.id ?? null
  // Dexie hands back a fresh object on every tick, so depending on the value
  // would reload the form — and discard whatever was half-typed into it —
  // every time any recipe anywhere changed. The id is what actually decides.
  const latest = useRef(recipe)
  latest.current = recipe

  /**
   * Loading has to hang off `open`, not off the sheet's own `onOpenChange`.
   * Radix only calls that for interactions it drives itself, so a sheet opened
   * by the parent setting a prop never fired it — which is why the editor
   * always came up holding whatever the last one left behind.
   */
  useEffect(() => {
    if (!open) return
    const current = latest.current
    setName(current?.name ?? '')
    setIngredients([])
    setMissing(0)
    setMethod('unweighed')
    if (!current) return

    // A recipe saved before the pot was weighed stored the raw total as its
    // finished weight. Reloading that number as if it had been weighed froze
    // the denominator: add an ingredient to such a recipe and its macros grew
    // while the weight they are divided by did not, so every portion logged
    // from it read heavier. Recognise the number for the assumption it was and
    // let it keep following the ingredients until somebody weighs the dish.
    // Oil included, because that is what an unweighed save stored: the pot is
    // its ingredients plus whatever the pans left in them. Comparing against the
    // ingredients alone made a fried recipe look weighed the moment it was
    // reopened, which freezes the denominator — the exact bug the paragraph
    // above exists to prevent.
    const rows = liveIngredients(current)
    const rawTotal = rows.reduce(
      (sum, i) => sum + i.rawWeightG + retainedOilG(i.rawWeightG, i.cooking ?? null),
      0,
    )
    const unweighed = Math.abs(current.cookedWeightG - rawTotal) < 1
    setCookedWeightG(unweighed ? null : current.cookedWeightG)

    let cancelled = false
    void (async () => {
      // `rows` above, not `current.ingredients` — see `liveIngredients`. A
      // retired row read back here is a duplicate on screen and a double
      // helping in the macros.
      const cached = await db.foods.bulkGet(rows.map((i) => i.foodId))
      // A recipe synced from the other phone names foods this one has never
      // held. Fetching them matters more than it looks: an ingredient silently
      // dropped here is an ingredient deleted from the recipe on the next save.
      const resolved = await Promise.all(
        rows.map(async (ing, idx) => cached[idx] ?? fetchFoodRemote(ing.foodId)),
      )
      // The fetches above can outlive the recipe they were started for.
      if (cancelled || latest.current?.id !== recipeId) return
      setIngredients(
        rows.flatMap((ing, idx) => {
          const food = resolved[idx]
          // Null is what a recipe written before the question existed carries;
          // on screen that is simply "Žalias", which is what it was worth.
          return food
            ? [{ key: ing.id, food, rawWeightG: ing.rawWeightG, cooking: ing.cooking ?? 'raw' }]
            : []
        }),
      )
      setMissing(rows.length - resolved.filter(Boolean).length)
    })()
    return () => {
      cancelled = true
    }
  }, [open, recipeId])

  const save = async () => {
    if (!name.trim() || !derived || ingredients.length === 0) return
    setSaving(true)
    try {
      await saveRecipe({
        id: recipe?.id,
        name: name.trim(),
        // Carried, not re-sent as nothing: the editor has no notes field, and
        // saveRecipe writes what it is given — so editing a recipe imported
        // from a link used to throw away its method text.
        notes: recipe?.notes ?? null,
        cookedWeightG: derived.cooked,
        // A line left at nothing is not an ingredient — and it is worse than
        // useless: the wire refuses a weight of zero, so one emptied field
        // would make every push from this phone fail from then on. The macro
        // preview already ignores them; so does what gets saved.
        //
        // `key` is the ingredient's own id — the row's id for one that was
        // loaded, a fresh one for a line just added. Dropping it made every
        // save look like the whole list had been replaced: the server retired
        // the rows it knew and stored a new set under new ids, which is one
        // full extra copy of the recipe sitting in the table per save.
        ingredients: ingredients
          .filter((i) => i.rawWeightG > 0)
          .map((i) => ({
            id: i.key,
            foodId: i.food.id,
            rawWeightG: i.rawWeightG,
            cooking: i.cooking,
          })),
      })
      void syncNow()
      onOpenChange(false)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title={recipe ? 'Redaguoti receptą' : 'Naujas receptas'}
      footer={
        <Button
          variant="primary"
          block
          size="lg"
          onClick={() => void save()}
          disabled={saving || !name.trim() || derived == null}
        >
          Išsaugoti
        </Button>
      }
    >
      <div className="flex flex-col gap-4 px-4 py-4">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Recepto pavadinimas"
          className="h-11 w-full rounded-[var(--radius-control)] border border-line bg-bg px-3 text-[1rem] text-ink placeholder:text-mute"
        />

        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-sm text-dim">Žali ingredientai</span>
            <button
              type="button"
              onClick={() => setPicking(true)}
              className="text-sm text-accent-text"
            >
              Pridėti
            </button>
          </div>
          {missing > 0 && (
            <p className="mb-1.5 text-xs text-warn">
              {missing === 1 ? 'Vieno ingrediento' : `${missing} ingredientų`} nepavyko įkelti —
              produktas ištrintas iš katalogo. Išsaugojus jis iš recepto dings.
            </p>
          )}
          {ingredients.length === 0 ? (
            <p className="rounded-[var(--radius-card)] border border-dashed border-line px-3 py-4 text-center text-sm text-mute">
              Dar nėra ingredientų
            </p>
          ) : (
            <Card>
              {ingredients.map((ing, i) => (
                <div
                  key={ing.key}
                  className="relative px-3 py-2 after:absolute after:bottom-0 after:left-3 after:right-0 after:h-px after:bg-line last:after:hidden"
                >
                  <div className="flex items-center gap-3">
                    <span className="min-w-0 flex-1 truncate text-base text-ink">
                      {foodDisplayName(ing.food)}
                    </span>
                    <IngredientWeightField
                      value={ing.rawWeightG}
                      unit={ing.food.unit}
                      pieceWeightG={ing.food.pieceWeightG ?? null}
                      onChange={(next) =>
                        setIngredients((list) =>
                          list.map((x, xi) => (xi === i ? { ...x, rawWeightG: next } : x)),
                        )
                      }
                    />
                    <span className="text-sm text-mute">
                      {ing.food.pieceWeightG != null ? 'vnt.' : ing.food.unit}
                    </span>
                    <button
                      type="button"
                      aria-label="Pašalinti"
                      onClick={() => setIngredients((list) => list.filter((_, xi) => xi !== i))}
                      className="text-mute"
                    >
                      <svg viewBox="0 0 16 16" className="size-4" fill="none" aria-hidden="true">
                        <path
                          d="M4 4l8 8M12 4l-8 8"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  </div>
                  {/*
                    Per ingredient, because a dish is one pot and rarely one pan:
                    the egg is boiled while the ham is fried. A native select is
                    the whole control — it costs one line per row, opens as the
                    phone's own wheel, and the only thing the answer changes is
                    the oil, which is shown beside it the moment it is not zero.
                  */}
                  <div className="mt-1 flex items-center gap-2">
                    <select
                      aria-label={`Kaip paruošta: ${foodDisplayName(ing.food)}`}
                      value={ing.cooking}
                      onChange={(e) =>
                        setIngredients((list) =>
                          list.map((x, xi) =>
                            xi === i ? { ...x, cooking: e.target.value as CookingMethod } : x,
                          ),
                        )
                      }
                      className="h-7 rounded-[var(--radius-control)] border border-line bg-bg px-1.5 text-xs text-dim"
                    >
                      {COOKING_ORDER.map((option) => (
                        <option key={option} value={option}>
                          {CHIP_LABEL[option]}
                        </option>
                      ))}
                    </select>
                    {retainedOilG(ing.rawWeightG, ing.cooking) > 0 && (
                      <span className="tnum text-xs text-mute">
                        + {retainedOilG(ing.rawWeightG, ing.cooking)} g aliejaus
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </Card>
          )}
        </div>

        <CookedWeightField
          rawTotalG={derived?.rawTotal ?? 0}
          method={method}
          onMethodChange={(next, suggested) => {
            setMethod(next)
            // Including when there is no estimate: "Nežinau" means the weight
            // falls back to the raw total, and keeping the last method's shrunk
            // figure left the field claiming one thing and the hint under it
            // another — with every macro ~28 % denser than the user was told.
            setCookedWeightG(suggested)
          }}
          value={cookedWeightG}
          onChange={setCookedWeightG}
        />

        {derived && (
          <Card className="px-3 py-3">
            <div className="flex items-baseline justify-between text-sm">
              <span className="text-mute">Žalias svoris</span>
              <span className="tnum text-ink">{Math.round(derived.rawTotal)} g</span>
            </div>
            {derived.oilG > 0 && (
              <div className="mt-1 flex items-baseline justify-between text-sm">
                <span className="text-mute">Aliejus keptuvėse</span>
                <span className="tnum text-ink">
                  {derived.oilG} g · {Math.round(derived.oilG * 9)} kcal
                </span>
              </div>
            )}
            <div className="mt-1 flex items-baseline justify-between text-sm">
              <span className="text-mute">Išeiga</span>
              <span className="tnum text-ink">
                {derived.weighed ? (
                  <>
                    {(derived.yield * 100).toFixed(0)}%{' '}
                    <span className="text-mute">
                      {derived.yield < 1 ? '(išgaravo)' : derived.yield > 1 ? '(sugėrė)' : ''}
                    </span>
                  </>
                ) : (
                  <span className="text-mute">dar nepasverta</span>
                )}
              </span>
            </div>
            <MacroPreview
              frame="plain"
              className="mt-3 border-t border-line pt-3"
              kcal={derived.per100g.kcal}
              proteinG={derived.per100g.protein}
              carbsG={derived.per100g.carbs}
              fatG={derived.per100g.fat}
            />
            <p className="mt-2 text-xs text-mute">100 g gatavo patiekalo</p>
          </Card>
        )}
      </div>

      <FoodPickerSheet
        open={picking}
        onOpenChange={setPicking}
        title="Pridėti ingredientą"
        // Stored on the phone the moment it is picked. An ingredient found by
        // searching the server has never been written here, and a recipe cannot
        // work out its own macros from a food it does not hold — the editor
        // showed the right preview and saved the wrong recipe.
        onPick={(food) => {
          void cacheFood(food)
          setIngredients((list) => [
            ...list,
            {
              key: crypto.randomUUID(),
              food,
              // One of them, when it is the kind of thing you count.
              rawWeightG: food.pieceWeightG ?? 100,
              cooking: 'raw' as const,
            },
          ])
        }}
      />
    </Sheet>
  )
}
